import { useEffect, useState } from 'react';
import { COUNTRY_MAP_BY_ISO } from '@/lib/countries';

interface GeolocationResult {
  countryName: string;
  iso: string;
  dial: string;
  // source indicates how the country was detected: 'timezone' | 'ip' | 'language'
  source: 'timezone' | 'ip' | 'language';
}

const TZ_TO_ISO: Record<string, string> = {
  'Africa/Abidjan': 'CI',
  'Africa/Accra': 'GH',
  'Africa/Bamako': 'ML',
  'Africa/Banjul': 'GM',
  'Africa/Bissau': 'GW',
  'Africa/Conakry': 'GN',
  'Africa/Dakar': 'SN',
  'Africa/Freetown': 'SL',
  'Africa/Lome': 'TG',
  'Africa/Monrovia': 'LR',
  'Africa/Nouakchott': 'MR',
  'Africa/Ouagadougou': 'BF',
  'Africa/Porto-Novo': 'BJ',
  'Africa/Sao_Tome': 'ST',
  'Africa/Tripoli': 'LY',
  'Africa/Tunis': 'TN',
  'Africa/Casablanca': 'MA',
  'Africa/Ndjamena': 'TD',
  'Africa/Douala': 'CM',
  'Africa/Brazzaville': 'CG',
  'Africa/Kinshasa': 'CD',
  'Africa/Lubumbashi': 'CD',
  'Africa/Kigali': 'RW',
  'Africa/Bujumbura': 'BI',
  'Africa/Libreville': 'GA',
  'Africa/Malabo': 'GQ',
  'Africa/Bangui': 'CF',
  'Africa/Harare': 'ZW',
  'Africa/Gaborone': 'BW',
  'Africa/Lusaka': 'ZM',
  'Africa/Blantyre': 'MW',
  'Africa/Maputo': 'MZ',
  'Africa/Mbabane': 'SZ',
  'Africa/Windhoek': 'NA',
  'Africa/Johannesburg': 'ZA',
  'Africa/Maseru': 'LS',
  'Africa/Cairo': 'EG',
  'Africa/Khartoum': 'SD',
  'Africa/Addis_Ababa': 'ET',
  'Africa/Nairobi': 'KE',
  'Africa/Dar_es_Salaam': 'TZ',
  'Africa/Kampala': 'UG',
  'Africa/Mogadishu': 'SO',
  'Africa/Djibouti': 'DJ',
  'Africa/Asmara': 'ER',
  'Africa/Juba': 'SS',
};

function detectFromTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TZ_TO_ISO[tz] ?? null;
  } catch {
    return null;
  }
}

function detectFromLanguage(): string | null {
  try {
    for (const lang of navigator.languages ?? [navigator.language]) {
      const parts = lang.split('-');
      if (parts.length === 2 && parts[1].length === 2) return parts[1];
    }
    return null;
  } catch {
    return null;
  }
}

export function useSignupGeolocation() {
  const [result, setResult] = useState<GeolocationResult | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const detect = async () => {
      let iso: string | null = null;
      let source: GeolocationResult['source'] | null = null;

      // Try timezone first
      const tzIso = detectFromTimezone();
      if (tzIso) {
        iso = tzIso;
        source = 'timezone';
      }

      // Then try IP geolocation if timezone didn't give an ISO
      let ipCountry: string | null = null;
      if (!iso) {
        try {
          // Prefer ipinfo first — some IP providers return more accurate country codes
          const res1 = await fetch('https://ipinfo.io/json', { signal: controller.signal });
          if (res1.ok) {
            const data1 = await res1.json();
            const code1: string | undefined = data1.country;
            if (code1 && COUNTRY_MAP_BY_ISO[code1]) {
              iso = code1;
              source = 'ip';
              ipCountry = code1;
            }
          }
        } catch {
          // ignore
        }
      }

      if (!iso) {
        try {
          const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
          if (res.ok) {
            const data = await res.json();
            const code: string | undefined = data.country_code;
            if (code && COUNTRY_MAP_BY_ISO[code]) {
              iso = code;
              source = 'ip';
              ipCountry = code;
            } else {
              ipCountry = code ?? null;
            }
          }
        } catch {
          // ignore
        }
      }

      // Finally, try language
      if (!iso) {
        const langIso = detectFromLanguage();
        if (langIso) {
          iso = langIso;
          source = 'language';
        }
      }

      if (!iso) return;

      const country = COUNTRY_MAP_BY_ISO[iso];
      if (country) {
        setResult({ countryName: country.name, iso: country.iso, dial: country.dial, source: source ?? 'language' });
      }
    };

    detect();
    return () => controller.abort();
  }, []);

  return result;
}
